/**
 * DynamicConditionEditor Component
 *
 * Editor for a single dynamic trade condition.
 * Matches SignalEditor behavior exactly:
 * - Pencil icon to the left of dropdown
 * - Red/green color based on validation state
 * - Hover enlarges pencil
 * - Click opens param popover
 *
 * Layout: [pencil] [dropdown] [summary] [x]
 */

import React, { useState, useRef } from "react";
import { Pencil, X } from "lucide-react";
import { DynamicConditionParamPopover } from "./DynamicConditionParamPopover";
import {
  ConfiguredDynamicCondition,
  DYNAMIC_CONDITION_REGISTRY,
  getDynamicConditionOptions,
  isDynamicConditionComplete,
  getDynamicConditionSummary,
} from "../../../../../config/signals";
import styles from "./SignalEditor.module.css";
import selectorStyles from "./SignalSelector.module.css";

interface DynamicConditionEditorProps {
  condition: ConfiguredDynamicCondition;
  onUpdate: (condition: ConfiguredDynamicCondition) => void;
  onRemove: () => void;
  currentLegIndex?: number;
}

export const DynamicConditionEditor: React.FC<DynamicConditionEditorProps> = ({
  condition,
  onUpdate,
  onRemove,
  currentLegIndex,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const pencilRef = useRef<HTMLButtonElement>(null);

  const schema = DYNAMIC_CONDITION_REGISTRY[condition.type];
  const hasParams = schema && Object.keys(schema.params).length > 0;
  const isComplete = isDynamicConditionComplete(condition, currentLegIndex);
  const summary = getDynamicConditionSummary(condition);

  const handleTypeChange = (newType: string) => {
    onUpdate({
      ...condition,
      type: newType,
      params: {}, // Reset params when type changes
    });
  };

  const handleParamsChange = (params: Record<string, unknown>) => {
    onUpdate({ ...condition, params });
  };

  // Determine pencil button state
  const getPencilState = (): "valid" | "invalid" | "none" => {
    if (!condition.type) return "none";
    if (!hasParams) return "none";
    return isComplete ? "valid" : "invalid";
  };

  const pencilState = getPencilState();
  const options = getDynamicConditionOptions();

  return (
    <div className={styles.container}>
      {/* Pencil area - fixed width to prevent layout shift */}
      {pencilState !== "none" ? (
        <div className={styles.pencilWrapper}>
          <button
            ref={pencilRef}
            className={`${styles.pencilButton} ${styles[pencilState]}`}
            onClick={() => setShowPopover(!showPopover)}
            aria-label="Edit parameters"
          >
            <Pencil />
          </button>

          {/* Popover - rendered via portal */}
          {showPopover && (
            <DynamicConditionParamPopover
              conditionType={condition.type}
              params={condition.params}
              onParamsChange={handleParamsChange}
              onClose={() => setShowPopover(false)}
              anchorRef={pencilRef}
              currentLegIndex={currentLegIndex}
            />
          )}
        </div>
      ) : (
        /* Empty spacer when no pencil to maintain consistent layout */
        <div className={styles.pencilSpacer} />
      )}

      {/* Condition selector dropdown */}
      <div className={styles.selectorWrapper}>
        <select
          className={selectorStyles.selector}
          value={condition.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          <option value="">Select condition...</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Summary preview */}
      {summary && (
        <span className={styles.summary} title={summary}>
          {summary}
        </span>
      )}

      {/* Remove button */}
      <button
        className={styles.removeButton}
        onClick={onRemove}
        aria-label="Remove condition"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
};

